import {
  FilingStatus,
  FilingStatusSchema,
} from '@/apis/family_application/requests'
import { selectFamilyApplication, UpdateIncomeDetails } from '@/app-storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/app-storage/program_settings/programSettingsSlice.ts'
import { AppDispatch, RootState } from '@/app-storage/store.ts'
import { useAppAlert } from '@/app/AppAlertProvider'
import { FILING_OPTIONS } from '@/app/common/utilities'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { AppRoutePaths } from '@/routing/route_paths'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import useTaxableIncomeDocumentParams from '../hooks/useTaxableIncomeDocumentParams'
import { SpouseInformation } from './SpouseInformation'

function isMarriedFilingSeparately(filing: FilingStatus) {
  return filing === 'individually'
}

const FormData = z
  .object({
    filingStatus: FilingStatusSchema,
    spouseFirstName: z.string().optional(),
    spouseLastName: z.string().optional(),
  })
  .refine(
    (data) => {
      if (isMarriedFilingSeparately(data.filingStatus)) {
        return !!data.spouseFirstName && data.spouseFirstName.length > 0
      }
      return true
    },
    {
      message: 'Spouse first name is required when filing separately',
      path: ['spouseFirstName'],
    }
  )
  .refine(
    (data) => {
      if (isMarriedFilingSeparately(data.filingStatus)) {
        return !!data.spouseLastName && data.spouseLastName.length > 0
      }
      return true
    },
    {
      message: 'Spouse last name is required when filing separately',
      path: ['spouseLastName'],
    }
  )

export type FilingStatusForm = z.infer<typeof FormData>

export const FilingStatusPage = () => {
  const { t: translate } = useTranslation()
  const ProgramId = useSelector(selectProgramID)
  const dispatch = useDispatch<AppDispatch>()
  const { fireAlert } = useAppAlert()
  const navigateTo = useNavigate()
  const application = useSelector(selectFamilyApplication)
  const { request } = useSelector((state: RootState) => state.FamilyApplication)
  const { onEnterForm } = useTaxableIncomeDocumentParams()

  const {
    register,

    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FilingStatusForm>({
    defaultValues: {
      spouseFirstName: application.spouse_first_name ?? undefined,
      spouseLastName: application.spouse_last_name ?? undefined,
      filingStatus:
        application.household_filed_federal_income ?? undefined,
    },
    resolver: zodResolver(FormData),
  })

  const onSubmit = async (data: FilingStatusForm) => {
    const response = await dispatch(
      UpdateIncomeDetails({
        program_id: ProgramId,
        household_filed_federal_income: data.filingStatus,
        ...(isMarriedFilingSeparately(data.filingStatus) && {
          spouse_name: data.spouseFirstName,
          spouse_last_name: data.spouseLastName,
        }),
      })
    )

    if (UpdateIncomeDetails.fulfilled.match(response)) {
      /*  if (data.filingStatus === 'did_not_file') {
        navigateTo(
          AppRoutePaths.family_application.income_verification.threshold
        )
        return
      } */
      if (data.filingStatus === 'did_not_file') {
        onEnterForm(
          AppRoutePaths.family_application.income_verification
            .capture_instructions,
          'nontaxable'
        )
      } else {
        navigateTo(
          AppRoutePaths.family_application.income_verification
            .social_security_number
        )
      }
    } else {
      fireAlert({
        variant: 'error',
        message: response.payload?.message,
      })
    }
  }

  const selectedFilingStatus = watch('filingStatus')

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('filing_status')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <section className="w-full">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <h2 className="text-xl font-medium text-gray-900 mb-10 sm:mb-5">
              {translate('your_household_file')}{' '}
            </h2>
            <div className="space-y-6 w-full">
              <p className="sr-only">{translate('filing_status_options')}</p>
              {FILING_OPTIONS.map((option) => (
                <div key={option.value}>
                  <div className="flex items-center">
                    <div>
                      <input
                        id={option.value}
                        type="radio"
                        value={option.value}
                        className="h-4 w-4 flex-shrink-0 border-gray-600 text-primary-600 focus:ring-primary-600"
                        {...register('filingStatus')}
                      />
                    </div>
                    <label htmlFor={option.value} className="ml-2 font-medium">
                      {translate(option.label)}
                    </label>
                  </div>
                  {isMarriedFilingSeparately(selectedFilingStatus) &&
                    option.value === 'individually' && (
                      <div className="mt-4 mb-4 pl-4 border-l border-gray-200">
                        <SpouseInformation
                          register={register} // Pass register
                          errors={errors} // Pass errors if needed
                        />
                      </div>
                    )}
                </div>
              ))}
            </div>

            {errors.filingStatus && (
              <p className="text-red-500 text-sm" role="alert">
                {translate('filing_status_is_required')}
              </p>
            )}
          </form>
        </section>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          forceDisabled={!!errors.filingStatus}
          onClick={handleSubmit(onSubmit)}
          status={request.status}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
